import React, { useEffect, useState, useCallback, useContext } from "react";
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  RefreshControl,
  Modal,
  TouchableOpacity,
  FlatList,
  Alert,
  Platform,
  useWindowDimensions
} from "react-native";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../../firebase";
import { AuthContext } from "../../Context/AuthContext";
import { useFocusEffect } from "@react-navigation/native";
import GlobalStyles from "../../components/StylenColor";
import sendTopicAssignmentEmail from "../../components/sendTopicAssignmentEmail";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";
import { useDesktopDetect, DashboardLayout, DesktopTheme, getPillarColor, getPillarBgColor } from "../../components/web";
import { Ionicons } from "@expo/vector-icons";

// Desktop Topic Card Component
const DesktopTopicCard = ({ dept, topics, topicToEmployees, onTopicClick, isAssigned }) => {
  const [hoveredTopic, setHoveredTopic] = useState(null);

  // Determine pillar based on department name
  const getPillarFromDept = (deptName) => {
    const lower = deptName?.toLowerCase() || '';
    if (lower.includes('environment') || lower.includes('energy') || lower.includes('waste') || lower.includes('emission')) return 'environmental';
    if (lower.includes('social') || lower.includes('hr') || lower.includes('safety') || lower.includes('community')) return 'social';
    if (lower.includes('governance') || lower.includes('compliance') || lower.includes('ethics') || lower.includes('board')) return 'governance';
    return 'environmental';
  };

  const pillar = getPillarFromDept(dept);
  const pillarColor = getPillarColor(pillar);
  const pillarBgColor = getPillarBgColor(pillar);

  return (
    <View style={[desktopStyles.topicCard, { borderLeftColor: pillarColor }]}>
      <View style={[desktopStyles.cardHeader, { backgroundColor: pillarBgColor }]}>
        <Text style={[desktopStyles.deptTitle, { color: pillarColor }]}>{dept}</Text>
        <View style={[desktopStyles.countBadge, { backgroundColor: pillarColor }]}>
          <Text style={desktopStyles.countText}>{topics.length}</Text>
        </View>
      </View>

      <View style={desktopStyles.topicsList}>
        {topics.map((topic) => (
          <TouchableOpacity
            key={topic}
            style={[
              desktopStyles.topicItem,
              hoveredTopic === topic && desktopStyles.topicItemHover,
              !isAssigned && desktopStyles.topicItemClickable,
            ]}
            onPress={() => !isAssigned && onTopicClick(dept, topic)}
            onMouseEnter={() => setHoveredTopic(topic)}
            onMouseLeave={() => setHoveredTopic(null)}
            disabled={isAssigned}
          >
            <View style={desktopStyles.topicRow}>
              {isAssigned ? (
                <Ionicons name="checkmark-circle" size={18} color={DesktopTheme.colors.success} />
              ) : (
                <Ionicons name="ellipse-outline" size={18} color={DesktopTheme.colors.textMuted} />
              )}
              <Text style={[
                desktopStyles.topicName,
                isAssigned && desktopStyles.topicNameAssigned
              ]}>{topic}</Text>
            </View>

            {isAssigned && topicToEmployees[topic] && (
              <View style={desktopStyles.assigneeInfo}>
                {topicToEmployees[topic].map((emp, idx) => (
                  <View key={idx} style={desktopStyles.assigneeBadge}>
                    <Text style={desktopStyles.assigneeText}>
                      {emp.name} ({emp.id})
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {!isAssigned && (
              <Text style={desktopStyles.clickToAssign}>Click to assign</Text>
            )}
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
};

// Desktop Assignment Modal
const DesktopAssignModal = ({
  visible,
  onClose,
  selectedTopic,
  deptEmployees,
  selectedEmployee,
  setSelectedEmployee,
  onAssign,
  assigning
}) => {
  const [hoveredEmployee, setHoveredEmployee] = useState(null);

  if (!visible) return null;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={desktopStyles.modalOverlay}>
        <View style={desktopStyles.modalContent}>
          <View style={desktopStyles.modalHeader}>
            <Text style={desktopStyles.modalTitle}>Assign Topic</Text>
            <TouchableOpacity style={desktopStyles.modalCloseBtn} onPress={onClose}>
              <Ionicons name="close" size={24} color={DesktopTheme.colors.textSecondary} />
            </TouchableOpacity>
          </View>

          <View style={desktopStyles.topicBadgeContainer}>
            <View style={desktopStyles.topicBadge}>
              <Text style={desktopStyles.topicBadgeText}>{selectedTopic}</Text>
            </View>
          </View>

          <Text style={desktopStyles.selectLabel}>Select an employee to assign this topic:</Text>

          <ScrollView style={desktopStyles.employeesList} showsVerticalScrollIndicator={false}>
            {deptEmployees.map((emp) => {
              const empId = emp.employeeId || emp.id;
              const isSelected = selectedEmployee?.id === emp.id;
              return (
                <TouchableOpacity
                  key={emp.id}
                  style={[
                    desktopStyles.employeeItem,
                    isSelected && desktopStyles.employeeItemSelected,
                    hoveredEmployee === emp.id && !isSelected && desktopStyles.employeeItemHover,
                  ]}
                  onPress={() => setSelectedEmployee(emp)}
                  onMouseEnter={() => setHoveredEmployee(emp.id)}
                  onMouseLeave={() => setHoveredEmployee(null)}
                >
                  <View style={desktopStyles.employeeAvatar}>
                    <Text style={desktopStyles.avatarText}>
                      {emp.name?.charAt(0)?.toUpperCase() || '?'}
                    </Text>
                  </View>
                  <View style={desktopStyles.employeeDetails}>
                    <Text style={desktopStyles.employeeName}>{emp.name}</Text>
                    <Text style={desktopStyles.employeeId}>ID: {empId}</Text>
                  </View>
                  {isSelected && (
                    <Ionicons name="checkmark-circle" size={24} color={DesktopTheme.colors.success} />
                  )}
                </TouchableOpacity>
              );
            })}
          </ScrollView>

          <View style={desktopStyles.modalActions}>
            <TouchableOpacity
              style={desktopStyles.cancelBtn}
              onPress={onClose}
            >
              <Text style={desktopStyles.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                desktopStyles.assignBtn,
                (!selectedEmployee || assigning) && desktopStyles.assignBtnDisabled,
              ]}
              onPress={onAssign}
              disabled={!selectedEmployee || assigning}
            >
              {assigning ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={desktopStyles.assignBtnText}>Assign Topic</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

// Desktop View Component
const DesktopEmployeeTopicStatusView = ({
  loading,
  selectedTopics,
  remainingTopics,
  topicToEmployees,
  onTopicClick,
  modalVisible,
  setModalVisible,
  selectedTopic,
  deptEmployees,
  selectedEmployee,
  setSelectedEmployee,
  assignTopic,
  assigning,
}) => {
  const totalAssigned = Object.values(selectedTopics).flat().length;
  const totalRemaining = Object.values(remainingTopics).flat().length;
  const totalTopics = totalAssigned + totalRemaining;

  return (
    <DashboardLayout currentRoute="EmployeeTopicStatus">
      <ScrollView style={desktopStyles.container} showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View style={desktopStyles.header}>
          <View style={desktopStyles.headerLeft}>
            <Text style={desktopStyles.pageTitle}>Topic Assignment</Text>
            <Text style={desktopStyles.pageSubtitle}>
              Manage ESG topic assignments across departments
            </Text>
          </View>
        </View>

        {/* Stats Row */}
        <View style={desktopStyles.statsRow}>
          <View style={desktopStyles.statCard}>
            <Ionicons name="documents-outline" size={24} color={DesktopTheme.colors.primary} />
            <Text style={desktopStyles.statValue}>{totalTopics}</Text>
            <Text style={desktopStyles.statLabel}>Total Topics</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Ionicons name="checkmark-done-outline" size={24} color={DesktopTheme.colors.success} />
            <Text style={[desktopStyles.statValue, { color: DesktopTheme.colors.success }]}>{totalAssigned}</Text>
            <Text style={desktopStyles.statLabel}>Assigned</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Ionicons name="time-outline" size={24} color={DesktopTheme.colors.warning} />
            <Text style={[desktopStyles.statValue, { color: DesktopTheme.colors.warning }]}>{totalRemaining}</Text>
            <Text style={desktopStyles.statLabel}>Remaining</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Ionicons name="business-outline" size={24} color={DesktopTheme.colors.info} />
            <Text style={desktopStyles.statValue}>{Object.keys({ ...selectedTopics, ...remainingTopics }).length}</Text>
            <Text style={desktopStyles.statLabel}>Departments</Text>
          </View>
        </View>

        {loading ? (
          <View style={desktopStyles.loadingContainer}>
            <ActivityIndicator size="large" color={DesktopTheme.colors.primary} />
            <Text style={desktopStyles.loadingText}>Loading topics...</Text>
          </View>
        ) : (
          <>
            {/* Remaining Topics Section */}
            <View style={desktopStyles.section}>
              <View style={desktopStyles.sectionHeader}>
                <Ionicons name="alert-circle-outline" size={22} color={DesktopTheme.colors.warning} />
                <Text style={desktopStyles.sectionTitle}>Unassigned Topics</Text>
                <View style={[desktopStyles.sectionBadge, { backgroundColor: DesktopTheme.colors.warning + '20' }]}>
                  <Text style={[desktopStyles.sectionBadgeText, { color: DesktopTheme.colors.warning }]}>
                    {totalRemaining} pending
                  </Text>
                </View>
              </View>

              {Object.keys(remainingTopics).length === 0 ? (
                <View style={desktopStyles.emptySection}>
                  <Ionicons name="checkmark-circle" size={40} color={DesktopTheme.colors.success} />
                  <Text style={desktopStyles.emptyText}>All topics have been assigned!</Text>
                </View>
              ) : (
                <View style={desktopStyles.cardsGrid}>
                  {Object.entries(remainingTopics).map(([dept, topics]) => (
                    <DesktopTopicCard
                      key={dept}
                      dept={dept}
                      topics={topics}
                      topicToEmployees={topicToEmployees}
                      onTopicClick={onTopicClick}
                      isAssigned={false}
                    />
                  ))}
                </View>
              )}
            </View>

            {/* Assigned Topics Section */}
            <View style={desktopStyles.section}>
              <View style={desktopStyles.sectionHeader}>
                <Ionicons name="checkmark-done-outline" size={22} color={DesktopTheme.colors.success} />
                <Text style={desktopStyles.sectionTitle}>Assigned Topics</Text>
                <View style={[desktopStyles.sectionBadge, { backgroundColor: DesktopTheme.colors.success + '20' }]}>
                  <Text style={[desktopStyles.sectionBadgeText, { color: DesktopTheme.colors.success }]}>
                    {totalAssigned} completed
                  </Text>
                </View>
              </View>

              {Object.keys(selectedTopics).length === 0 ? (
                <View style={desktopStyles.emptySection}>
                  <Ionicons name="document-outline" size={40} color={DesktopTheme.colors.textMuted} />
                  <Text style={desktopStyles.emptyText}>No topics assigned yet.</Text>
                </View>
              ) : (
                <View style={desktopStyles.cardsGrid}>
                  {Object.entries(selectedTopics).map(([dept, topics]) => (
                    <DesktopTopicCard
                      key={dept}
                      dept={dept}
                      topics={topics}
                      topicToEmployees={topicToEmployees}
                      onTopicClick={() => {}}
                      isAssigned={true}
                    />
                  ))}
                </View>
              )}
            </View>
          </>
        )}
      </ScrollView>

      <DesktopAssignModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        selectedTopic={selectedTopic}
        deptEmployees={deptEmployees}
        selectedEmployee={selectedEmployee}
        setSelectedEmployee={setSelectedEmployee}
        onAssign={assignTopic}
        assigning={assigning}
      />
    </DashboardLayout>
  );
};

// Desktop Styles
const desktopStyles = StyleSheet.create({
  container: {
    flex: 1,
    padding: DesktopTheme.spacing.lg,
    backgroundColor: DesktopTheme.colors.contentBg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: DesktopTheme.spacing.lg,
  },
  headerLeft: {
    flex: 1,
  },
  pageTitle: {
    fontSize: DesktopTheme.typography.fontSize.h2,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
  },
  statsRow: {
    flexDirection: 'row',
    gap: DesktopTheme.spacing.md,
    marginBottom: DesktopTheme.spacing.lg,
  },
  statCard: {
    flex: 1,
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: DesktopTheme.spacing.md,
    alignItems: 'center',
    ...DesktopTheme.shadows.sm,
  },
  statValue: {
    fontSize: DesktopTheme.typography.fontSize.h3,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginTop: DesktopTheme.spacing.xs,
  },
  statLabel: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
    marginTop: 2,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 100,
  },
  loadingText: {
    marginTop: DesktopTheme.spacing.md,
    fontSize: DesktopTheme.typography.fontSize.body,
    color: DesktopTheme.colors.textSecondary,
  },
  section: {
    marginBottom: DesktopTheme.spacing.xl,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.sm,
    marginBottom: DesktopTheme.spacing.md,
  },
  sectionTitle: {
    fontSize: DesktopTheme.typography.fontSize.h4,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    flex: 1,
  },
  sectionBadge: {
    paddingVertical: 4,
    paddingHorizontal: DesktopTheme.spacing.sm,
    borderRadius: DesktopTheme.borderRadius.full,
  },
  sectionBadgeText: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontWeight: '600',
  },
  emptySection: {
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: DesktopTheme.spacing.xl,
    alignItems: 'center',
    ...DesktopTheme.shadows.sm,
  },
  emptyText: {
    marginTop: DesktopTheme.spacing.sm,
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
  },
  cardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: DesktopTheme.spacing.md,
  },
  topicCard: {
    width: 350,
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    borderLeftWidth: 4,
    overflow: 'hidden',
    ...DesktopTheme.shadows.card,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: DesktopTheme.spacing.md,
  },
  deptTitle: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontWeight: '700',
  },
  countBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  countText: {
    color: '#ffffff',
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontWeight: '700',
  },
  topicsList: {
    padding: DesktopTheme.spacing.sm,
    gap: DesktopTheme.spacing.xs,
  },
  topicItem: {
    padding: DesktopTheme.spacing.sm,
    borderRadius: DesktopTheme.borderRadius.md,
    backgroundColor: DesktopTheme.colors.contentBg,
  },
  topicItemHover: {
    backgroundColor: DesktopTheme.colors.primaryLight,
  },
  topicItemClickable: {
    cursor: 'pointer',
  },
  topicRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.sm,
  },
  topicName: {
    flex: 1,
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textPrimary,
  },
  topicNameAssigned: {
    color: DesktopTheme.colors.success,
  },
  assigneeInfo: {
    marginTop: DesktopTheme.spacing.xs,
    marginLeft: 26,
  },
  assigneeBadge: {
    backgroundColor: DesktopTheme.colors.border,
    paddingVertical: 2,
    paddingHorizontal: DesktopTheme.spacing.sm,
    borderRadius: DesktopTheme.borderRadius.sm,
    marginTop: 2,
  },
  assigneeText: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
    fontStyle: 'italic',
  },
  clickToAssign: {
    marginTop: DesktopTheme.spacing.xs,
    marginLeft: 26,
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.primary,
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.xl,
    padding: DesktopTheme.spacing.lg,
    width: 480,
    maxHeight: '80%',
    ...DesktopTheme.shadows.lg,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: DesktopTheme.spacing.md,
  },
  modalTitle: {
    fontSize: DesktopTheme.typography.fontSize.h3,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
  },
  modalCloseBtn: {
    padding: DesktopTheme.spacing.xs,
  },
  topicBadgeContainer: {
    alignItems: 'center',
    marginBottom: DesktopTheme.spacing.md,
  },
  topicBadge: {
    backgroundColor: DesktopTheme.colors.primaryLight,
    paddingVertical: DesktopTheme.spacing.sm,
    paddingHorizontal: DesktopTheme.spacing.lg,
    borderRadius: DesktopTheme.borderRadius.full,
  },
  topicBadgeText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.primary,
    fontWeight: '600',
  },
  selectLabel: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
    marginBottom: DesktopTheme.spacing.md,
  },
  employeesList: {
    maxHeight: 300,
  },
  employeeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: DesktopTheme.spacing.md,
    borderRadius: DesktopTheme.borderRadius.md,
    marginBottom: DesktopTheme.spacing.sm,
    backgroundColor: DesktopTheme.colors.contentBg,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  employeeItemHover: {
    backgroundColor: DesktopTheme.colors.border,
  },
  employeeItemSelected: {
    backgroundColor: DesktopTheme.colors.primaryLight,
    borderColor: DesktopTheme.colors.primary,
  },
  employeeAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: DesktopTheme.colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: DesktopTheme.spacing.md,
  },
  avatarText: {
    color: '#ffffff',
    fontSize: DesktopTheme.typography.fontSize.bodyLarge,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontWeight: '700',
  },
  employeeDetails: {
    flex: 1,
  },
  employeeName: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    fontWeight: '600',
  },
  employeeId: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
  },
  modalActions: {
    flexDirection: 'row',
    gap: DesktopTheme.spacing.md,
    marginTop: DesktopTheme.spacing.lg,
  },
  cancelBtn: {
    flex: 1,
    paddingVertical: DesktopTheme.spacing.md,
    borderRadius: DesktopTheme.borderRadius.md,
    backgroundColor: DesktopTheme.colors.border,
    alignItems: 'center',
  },
  cancelBtnText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
    fontWeight: '600',
  },
  assignBtn: {
    flex: 1,
    paddingVertical: DesktopTheme.spacing.md,
    borderRadius: DesktopTheme.borderRadius.md,
    backgroundColor: DesktopTheme.colors.primary,
    alignItems: 'center',
  },
  assignBtnDisabled: {
    backgroundColor: DesktopTheme.colors.textMuted,
  },
  assignBtnText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: '#ffffff',
    fontWeight: '600',
  },
});

export default function EmployeeTopicStatus() {
  const { companyId, companyName } = useContext(AuthContext);
  const [departmentData, setDepartmentData] = useState({});
  const [topicToEmployees, setTopicToEmployees] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptEmployees, setDeptEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const { width } = useWindowDimensions();
  const isWeb = Platform.OS === "web";
  const isMobileWeb = isWeb && width <= 768;
  const isDesktopWeb = useDesktopDetect();
  
  const groupIntoPairs = (items) => {
    const pairs = [];
    for (let i = 0; i < items.length; i += 2) {
      pairs.push(items.slice(i, i + 2));
    }
    return pairs;
  };
  const showAlert = (title, message) => {
    if (Platform.OS === "web") {
      window.alert(`${title}\n\n${message}`);
    } else {
      showAlert(title, message);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const deptDoc = await getDoc(doc(db, "Dept&Topic", "departments"));
      const deptData = deptDoc.exists() ? deptDoc.data() : {};

      const empSnap = await getDocs(
        collection(db, `companies/${companyId}/employees`)
      );

      const topicMap = {};

      empSnap.forEach((doc) => {
        const data = doc.data();
        const employeeId = data.employeeId || doc.id;
        const topics = Array.isArray(data.topic) ? data.topic : [];

        topics.forEach((topic) => {
          if (!topicMap[topic]) {
            topicMap[topic] = [];
          }
          topicMap[topic].push({
            id: employeeId,
            name: data.name || "Unnamed",
          });
        });
      });

      setDepartmentData(deptData);
      setTopicToEmployees(topicMap);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Refresh on screen focus
  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [companyId])
  );

  // Pull to refresh
  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchData();
  }, []);

  const getSelectedTopicsByDept = () => {
    const selected = {};
    Object.entries(departmentData).forEach(([dept, topics]) => {
      const filtered = topics.filter((topic) => topicToEmployees[topic]);
      if (filtered.length > 0) {
        selected[dept] = filtered;
      }
    });
    return selected;
  };

  const getRemainingTopics = () => {
    const remaining = {};
    Object.entries(departmentData).forEach(([dept, topics]) => {
      const filtered = topics.filter((topic) => !topicToEmployees[topic]);
      if (filtered.length > 0) {
        remaining[dept] = filtered;
      }
    });
    return remaining;
  };

  const selectedTopics = getSelectedTopicsByDept();
  const remainingTopics = getRemainingTopics();

  const handleTopicClick = async (dept, topic) => {
    try {
      setSelectedDept(dept);
      setSelectedTopic(topic);
      setModalVisible(true);
      const empSnap = await getDocs(
        collection(db, `companies/${companyId}/employees`)
      );
      const filtered = [];
      empSnap.forEach((doc) => {
        const data = doc.data();
        if (data.department === dept) {
          filtered.push({ id: doc.id, ...data });
        }
      });
      setDeptEmployees(filtered);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  const assignTopic = async () => {
    if (!selectedEmployee || !selectedTopic) return;

    try {
      setAssigning(true);
      const empRef = doc(
        db,
        `companies/${companyId}/employees`,
        selectedEmployee.id
      );
      await updateDoc(empRef, {
        topic: arrayUnion(selectedTopic),
      });

      await sendTopicAssignmentEmail(
        selectedEmployee.email,
        selectedEmployee.name,
        selectedTopic,
        companyName // replace with actual variable from your context or props
      );

      showAlert(
        "Success",
        `Assigned "${selectedTopic}" to ${selectedEmployee.name}`
      );
      setModalVisible(false);
      setSelectedEmployee(null);
      fetchData(); // Refresh the list
    } catch (error) {
      console.error("Assignment error:", error);
      showAlert("Error", "Failed to assign topic.");
    } finally {
      setAssigning(false);
    }
  };

  // Desktop Web View
  if (isDesktopWeb) {
    return (
      <DesktopEmployeeTopicStatusView
        loading={loading}
        selectedTopics={selectedTopics}
        remainingTopics={remainingTopics}
        topicToEmployees={topicToEmployees}
        onTopicClick={handleTopicClick}
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        selectedTopic={selectedTopic}
        deptEmployees={deptEmployees}
        selectedEmployee={selectedEmployee}
        setSelectedEmployee={setSelectedEmployee}
        assignTopic={assignTopic}
        assigning={assigning}
      />
    );
  }

  // Mobile Loading
  if (loading) {
    return (
      <ActivityIndicator
        size="large"
        style={{
          marginTop: 20,
          backgroundColor: GlobalStyles.GlobalBg.backgroundColor,
          flex: 1,
        }}
      />
    );
  }

  // Mobile View
  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={{ paddingBottom: 100 }}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {/* Selected Topics Section */}
      <Text style={[styles.heading, GlobalStyles.titleTxtOffset]}>
        Selected Topics (by Department)
      </Text>
      {Object.keys(selectedTopics).length === 0 ? (
        <Text style={styles.subText}>No topics selected yet.</Text>
      ) : isWeb && !isMobileWeb ? (
        groupIntoPairs(Object.entries(selectedTopics)).map((pair, rowIndex) => (
          <View key={rowIndex} style={styles.rowWrapper}>
            {pair.map(([dept, topics]) => (
              <View key={dept} style={[styles.card, styles.cardWeb]}>
                <Text style={styles.department}>{dept}</Text>
                {topics.map((topic) => (
                  <Text key={topic} style={styles.topic}>
                    ➙ {topic} {"\n"}
                    <View style={styles.employeeListWrapper}>
                      <Text style={styles.employeeList}>
                        Selected by:{" "}
                        {topicToEmployees[topic]
                          .map((emp) => `${emp.name} ${emp.id}`)
                          .join(", ")}
                      </Text>
                    </View>
                  </Text>
                ))}
              </View>
            ))}
          </View>
        ))
      ) : (
        Object.entries(selectedTopics).map(([dept, topics]) => (
          <View key={dept} style={styles.card}>
            <Text style={styles.department}>{dept}</Text>
            {topics.map((topic) => (
              <Text key={topic} style={styles.topic}>
                ➙ {topic} {"\n"}
                <View style={styles.employeeListWrapper}>
                  <Text style={styles.employeeList}>
                    Selected by:{" "}
                    {topicToEmployees[topic]
                      .map((emp) => `${emp.name} ${emp.id}`)
                      .join(", ")}
                  </Text>
                </View>
              </Text>
            ))}
          </View>
        ))
      )}

      {/* Remaining Topics Section */}
      <Text style={[styles.heading, GlobalStyles.titleTxtOffset]}>
        Remaining Topics (Unassigned)
      </Text>
      {Object.keys(remainingTopics).length === 0 ? (
        <Text style={styles.subText}>All topics have been assigned.</Text>
      ) : isWeb && !isMobileWeb ? (
        groupIntoPairs(Object.entries(remainingTopics)).map(
          (pair, rowIndex) => (
            <View key={rowIndex} style={styles.rowWrapper}>
              {pair.map(([dept, topics]) => (
                <View key={dept} style={[styles.card, styles.cardWeb]}>
                  <Text style={styles.department}>{dept}</Text>
                  {topics.map((topic) => (
                    <TouchableOpacity
                      key={topic}
                      onPress={() => handleTopicClick(dept, topic)}
                    >
                      <Text style={styles.topic}>{`• ${topic}`}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              ))}
            </View>
          )
        )
      ) : (
        Object.entries(remainingTopics).map(([dept, topics]) => (
          <View key={dept} style={styles.card}>
            <Text style={styles.department}>{dept}</Text>
            {topics.map((topic) => (
              <TouchableOpacity
                key={topic}
                onPress={() => handleTopicClick(dept, topic)}
              >
                <Text style={styles.topic}>{`• ${topic}`}</Text>
              </TouchableOpacity>
            ))}
          </View>
        ))
      )}

      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={[styles.modalOverlay]}>
          <View
            style={[
              styles.modalContent,
              isWeb && !isMobileWeb && { width: "60%" },
            ]}
          >
            {/* Cancel Button */}
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setModalVisible(false)}
            >
              <Text style={styles.modalCloseText}>✕</Text>
            </TouchableOpacity>

            <Text style={styles.modalTitle}>Assign "{selectedTopic}"</Text>

            <FlatList
              data={deptEmployees}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => {
                const empId = item.employeeId || item.id;
                const isSelected = selectedEmployee?.id === item.id;
                return (
                  <TouchableOpacity
                    style={[
                      styles.employeeItem,
                      selectedEmployee?.id === item.id &&
                        styles.selectedEmployee,
                    ]}
                    onPress={() => setSelectedEmployee(item)}
                  >
                    <View style={styles.employeeRow}>
                      <Text style={styles.employeeOption}>
                        {item.name} ({empId})
                      </Text>
                      {isSelected && <Text style={styles.tick}>✓</Text>}
                    </View>
                  </TouchableOpacity>
                );
              }}
            />

            <TouchableOpacity
              style={[
                styles.assignButton,
                GlobalStyles.btnBgShadow,
                assigning && { opacity: 0.7 },
              ]}
              onPress={assignTopic}
              disabled={!selectedEmployee || assigning}
            >
              {assigning ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text
                  style={[styles.assignButtonText, GlobalStyles.btntxtoffset]}
                >
                  Assign
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: GlobalStyles.GlobalBg.backgroundColor,
  },
  heading: {
    fontSize: GlobalStyles.fontSize.md,
    color: GlobalStyles.btnColor.backgroundColor,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginTop: 5,
    marginBottom: 12,
    paddingVertical: 1,
  },
  subText: {
    color: "gray",
    fontStyle: "italic",
    marginBottom: 10,
    paddingLeft: 18,
    fontSize: GlobalStyles.fontSize.sm,
  },
  card: {
    marginBottom: 12,
    padding: 12,
    backgroundColor: "#effdee",
    borderRadius: 8,
  },
  topic: {
    fontSize: GlobalStyles.fontSize.xss,
    fontFamily: GlobalStyles.inpFont.fontFamily,
    paddingVertical: 1,
    marginTop: 4,
  },
  employeeListWrapper: {
    marginLeft: 30,
  },
  employeeList: {
    color: GlobalStyles.inpColor.colors.selection,
    fontSize: GlobalStyles.fontSize.xss,
    fontFamily: GlobalStyles.inpFont.fontFamily,
    fontStyle: "italic",
    paddingLeft: 10,
  },
  department: {
    fontSize: GlobalStyles.fontSize.xs,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginBottom: 2,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#fff",
    padding: 20,
    borderRadius: 10,
    width: "85%",
    maxHeight: "80%",
    position: "relative",
  },
  modalTitle: {
    fontSize: GlobalStyles.fontSize.sm,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginBottom: 24,
    textAlign: "center",
    top: 15,
  },
  modalCloseButton: {
    position: "absolute",
    top: 5,
    right: 10,
    zIndex: 1,
    padding: 5,
  },
  modalCloseText: {
    fontSize: GlobalStyles.fontSize.lg,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  employeeItem: {
    padding: 10,
    borderBottomColor: "#ccc",
    borderBottomWidth: 1,
  },
  employeeOption: {
    fontSize: GlobalStyles.fontSize.xs,
    fontFamily: GlobalStyles.inpFont.fontFamily,
  },
  selectedEmployee: {
    backgroundColor: "#effdee",
  },
  assignButton: {
    marginTop: 15,
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    padding: 10,
    borderRadius: 5,
    alignItems: "center",
  },
  assignButtonText: {
    color: "#fff",
    fontSize: GlobalStyles.fontSize.xs,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  employeeRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  tick: {
    fontSize: GlobalStyles.fontSize.topTitle,
    color: "green",
    fontWeight: "bold",
  },
  rowWrapper: {
    flexDirection: "row",
    alignItems: "flex-start", // ✨ Allow independent height
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 12,
  },
  cardWeb: {
    flex: 1,
    minWidth: "48%",
    marginHorizontal: 4,
    alignSelf: "flex-start", // ✨ Ensures card height fits content
  },
});
